import { card, classifyMeld, isWild, solveHand, type MeldContext } from '@rummy/engine';
import type { TableView } from '@rummy/protocol';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HandResultList } from '../components/HandResultList.tsx';
import { PlayingCard } from '../components/PlayingCard.tsx';
import { useCountUp } from '../hooks/useCountUp.ts';
import { useI18n } from '../i18n/index.tsx';

/** Long enough to read the scores, short enough that nobody is waiting on it. */
const AUTO_CONTINUE_SECONDS = 12;

export interface HandSummaryProps {
  table: TableView;
  accountId: string;
  onContinue: () => void;
  canContinue?: boolean;
  waitingForHost?: boolean;
}

/**
 * §6.1 screen 5 — each player's final hand laid out grouped, with points.
 *
 * `bestPossible` is shown next to the score when they differ: a player scored
 * on a rushed arrangement deserves to see what the arrangement cost them,
 * rather than being left to suspect the server.
 */
export function HandSummary({
  table,
  accountId,
  onContinue,
  canContinue = true,
  waitingForHost = false,
}: HandSummaryProps) {
  const { t } = useI18n();
  const ctx = useMemo<MeldContext>(
    () => ({ config: table.rules, wildRank: table.wildRank }),
    [table.rules, table.wildRank],
  );

  const results = table.results ?? [];
  const bySeat = new Map(table.seats.map((s) => [s.id, s]));
  const ordered = [...results].sort((a, b) => a.points - b.points);
  const [coachOpen, setCoachOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const winner = ordered.find((result) => result.outcome === 'won') ?? ordered[0];

  /**
   * The winning hand, with the pure sequences last.
   *
   * The groups land in order, so whichever is last is the one the moment ends
   * on — and a pure sequence is the thing the whole hand was about. Dealing
   * them in submitted order ends on whatever the player happened to arrange
   * last, which is nothing.
   */
  const winnerGroups = useMemo(() => {
    if (!winner) return [];
    const groups =
      winner.groups.length > 0 ? winner.groups : [table.reveal?.[winner.player] ?? []];
    return [...groups].sort(
      (a, b) =>
        Number(classifyMeld(a, ctx)?.kind === 'pure-sequence') -
        Number(classifyMeld(b, ctx)?.kind === 'pure-sequence'),
    );
  }, [winner, table.reveal, ctx]);

  /*
   * A hand that was won by declaration opens showing the hand that won it. It
   * was behind a button, which is a strange place to put the only part of the
   * screen anybody actually wants to look at. A hand that ended by attrition
   * has no winning arrangement to show and stays closed.
   */
  const declared = winner?.outcome === 'won' && winnerGroups.some((g) => g.length > 0);
  const [replayOpen, setReplayOpen] = useState(declared);
  const yourResult = results.find((result) => result.player === accountId);
  const yourReveal = table.reveal?.[accountId] ?? [];
  const coaching = useMemo(() => {
    if (!yourResult || yourReveal.length === 0 || yourResult.outcome === 'won') return null;
    const optimal = solveHand(yourReveal, ctx);
    const currentPure = yourResult.groups.filter(
      (group) => classifyMeld(group, ctx)?.kind === 'pure-sequence',
    ).length;
    const notes: ('grouping' | 'pure' | 'deadwood')[] = [];
    if (yourResult.bestPossible < yourResult.points) notes.push('grouping');
    if (currentPure === 0 && optimal.arrangement.pureSequences > 0) notes.push('pure');
    if (optimal.arrangement.deadwood.length > 0) notes.push('deadwood');
    if (notes.length === 0) return null;
    return { notes, optimal };
  }, [yourResult, yourReveal, ctx]);
  const decisionTrace = useMemo(
    () => decisionTraceFor(table.publicHistory ?? table.events, accountId),
    [table.publicHistory, table.events, accountId],
  );
  const expertNotes = table.review ?? [];

  /**
   * The host-gated countdown. Only runs for the person who can actually act on
   * it, and only while nothing else on this screen is open — a host reading the
   * expert review should not have the next hand dealt out from under them.
   */
  const [held, setHeld] = useState(false);
  const paused = held || replayOpen || reviewOpen || coachOpen;
  const [autoIn, setAutoIn] = useState<number | null>(
    () => (canContinue && !waitingForHost ? AUTO_CONTINUE_SECONDS : null),
  );
  useEffect(() => {
    if (!canContinue || waitingForHost) {
      setAutoIn(null);
      return;
    }
    if (paused) return;
    const id = setInterval(() => {
      setAutoIn((left) => (left === null ? null : Math.max(0, left - 1)));
    }, 1000);
    return () => clearInterval(id);
  }, [canContinue, waitingForHost, paused]);

  const fired = useRef(false);
  useEffect(() => {
    if (autoIn !== 0 || fired.current) return;
    fired.current = true;
    onContinue();
  }, [autoIn, onContinue]);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={t('summary.heading', { n: table.ordinal + 1 })}>
      <div className="sheet__panel">
        <header className="sheet__head sheet__head--summary">
          <h2>{t('summary.heading', { n: table.ordinal + 1 })}</h2>
          {winnerGroups.some((group) => group.length > 0) && (
            <button type="button" className="btn btn--ghost" onClick={() => setReplayOpen((open) => !open)}>
              {replayOpen ? t('summary.replay.close') : t('summary.replay')}
            </button>
          )}
          {decisionTrace.length > 0 && (
            <button
              type="button"
              className={`btn btn--ghost${expertNotes.length > 0 ? ' btn--noted' : ''}`}
              onClick={() => setReviewOpen((open) => !open)}
            >
              {reviewOpen ? t('summary.review.close') : t('summary.review')}
              {expertNotes.length > 0 && !reviewOpen && (
                <span className="btn__badge">{expertNotes.length}</span>
              )}
            </button>
          )}
          {/*
            An auto-continue countdown, because with `pauseBetweenHands` on a
            distracted host stalls five other people indefinitely and nothing on
            screen says so. Hovering, focusing or touching the button holds it —
            a host who is reading the scores is not idle.
          */}
          <button
            type="button"
            className={`btn btn--primary${autoIn !== null ? ' btn--counting' : ''}`}
            disabled={!canContinue}
            onClick={onContinue}
            onPointerEnter={() => setHeld(true)}
            onPointerLeave={() => setHeld(false)}
            onFocus={() => setHeld(true)}
            onBlur={() => setHeld(false)}
          >
            {waitingForHost
              ? t('table.waitingForHost')
              : autoIn !== null
                ? t('summary.continue.in', { seconds: autoIn })
                : t('summary.continue')}
          </button>
        </header>

        {replayOpen && winner && (
          <section className="results__replay" aria-live="polite">
            <div className="results__replay-head">
              <span className="results__replay-orb" aria-hidden="true">✦</span>
              <div>
                <strong>{t('summary.replay.title', { name: bySeat.get(winner.player)?.handle ?? winner.player })}</strong>
                <p>{t('summary.replay.copy')}</p>
              </div>
            </div>
            <div className="results__replay-hand" dir="ltr">
              {winnerGroups.map((group, groupIndex) => (
                <div key={`${groupIndex}-${group.join('-')}`} className="results__replay-group">
                  {group.map((id, cardIndex) => (
                    <span
                      key={id}
                      className="results__replay-card"
                      style={{ animationDelay: `${(groupIndex * 4 + cardIndex) * 68}ms` }}
                    >
                      <PlayingCard id={id} wild={isWild(id, ctx)} feature />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </section>
        )}

        {reviewOpen && (
          <section className="results__review" aria-label={t('summary.review.title')}>
            <div className="results__replay-head">
              <span className="results__replay-orb" aria-hidden="true">⌁</span>
              <div>
                <strong>{t('summary.review.title')}</strong>
                <p>{t('summary.review.copy')}</p>
              </div>
            </div>

            {/*
              The turns where the player's throw diverged most from the expert
              policy. Computed on the server after the hand, from this player's
              own cards only, and against a policy that never saw the closed
              deck — so it is a claim about play from the same information the
              player had, not an oracle marking their homework.
            */}
            {expertNotes.length > 0 && (
              <ul className="results__expert">
                {expertNotes.map((turn) => (
                  <li
                    key={`expert-${turn.turn}`}
                    className={turn.missedDeclaration ? 'is-major' : ''}
                  >
                    <span className="results__review-turn">{turn.turn}</span>
                    {turn.missedDeclaration ? (
                      <span>
                        {t('summary.expert.missedDeclaration', {
                          card: displayCard(turn.expertDiscard),
                        })}
                      </span>
                    ) : (
                      <span>
                        {t('summary.expert.instead', {
                          yours: displayCard(turn.discard),
                          theirs: displayCard(turn.expertDiscard),
                          points: turn.savings,
                        })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <ol>
              {decisionTrace.map((turn, index) => (
                <li key={`${turn.discard ?? turn.drawCard ?? 'turn'}-${index}`}>
                  <span className="results__review-turn">{index + 1}</span>
                  <span>{turn.drawSource === 'discard'
                    ? t('summary.review.open', { card: displayCard(turn.drawCard) })
                    : t('summary.review.closed')}</span>
                  {turn.discard && <strong>{t('summary.review.discard', { card: displayCard(turn.discard) })}</strong>}
                </li>
              ))}
            </ol>
          </section>
        )}

        {coaching && (
          <section className="results__coach" aria-label={t('summary.coach.heading')}>
            <div className="results__coach-copy">
              <span className="results__coach-orb" aria-hidden="true">✦</span>
              <div>
                <strong>{t('summary.coach.heading')}</strong>
                {coaching.notes.map((note) => (
                  <p key={note}>
                    {note === 'grouping'
                      ? t('summary.coach.grouping', { points: yourResult!.points - yourResult!.bestPossible })
                      : note === 'pure'
                        ? t('summary.coach.pure')
                        : t('summary.coach.deadwood', { count: coaching.optimal.arrangement.deadwood.length })}
                  </p>
                ))}
              </div>
            </div>
            <button type="button" className="btn btn--small" onClick={() => setCoachOpen((open) => !open)}>
              {coachOpen ? t('summary.coach.hide') : t('summary.coach.show')}
            </button>
            {coachOpen && (
              <div className="results__coach-hand" dir="ltr">
                {coaching.optimal.arrangement.groups.map((group, groupIndex) => (
                  <div key={`${groupIndex}-${group.join('-')}`} className="results__group results__group--valid">
                    {group.map((id) => <PlayingCard key={id} id={id} wild={isWild(id, ctx)} feature />)}
                  </div>
                ))}
                {coaching.optimal.arrangement.deadwood.length > 0 && (
                  <div className="results__group results__group--loose">
                    {coaching.optimal.arrangement.deadwood.map((id) => <PlayingCard key={id} id={id} wild={isWild(id, ctx)} feature dimmed />)}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        <HandResultList
          results={results}
          reveal={table.reveal}
          handleFor={(player) => bySeat.get(player)?.handle ?? player}
          accountId={accountId}
          ctx={ctx}
        />

        {table.session && (
          <table className="standings">
            <caption className="visually-hidden">{t('summary.total')}</caption>
            <tbody>
              {table.seats.map((seat) => {
                const standing = table.session!.standings[seat.id];
                // §3.8 — the one place a total can go *down*. A player who
                // watched 198 become 190 with nothing said has been handed a
                // bug, however correct the arithmetic was, so the hand it
                // happens in says so outright.
                const reentered =
                  standing?.reentry && standing.reentry.hand === table.session!.handsPlayed
                    ? standing.reentry
                    : null;
                return (
                  <tr key={seat.id} className={seat.eliminated ? 'is-out' : ''}>
                    <th scope="row">
                      {seat.handle}
                      {reentered && (
                        <small className="standings__note">
                          {t('summary.reentry', {
                            from: reentered.from,
                            to: reentered.to,
                          })}
                        </small>
                      )}
                    </th>
                    <td>
                      {/* The change is the news, so the change is what moves —
                          from this player's total before the hand to their
                          total after it. */}
                      <RunningScore
                        to={standing?.score ?? 0}
                        delta={results.find((r) => r.player === seat.id)?.points ?? 0}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * A running session total, animating only the part that changed.
 *
 * A re-entry in pool 201 can move a score *down* (§3.8), so this must not
 * assume the delta is positive — it counts from wherever the player was to
 * wherever they now are, in whichever direction that is.
 */
function RunningScore({ to, delta }: { to: number; delta: number }) {
  const shown = useCountUp(to, { from: to - delta, duration: 700, delay: 260 });
  return (
    <span className={delta > 0 ? 'standings__score is-rising' : 'standings__score'}>
      {shown}
    </span>
  );
}

function decisionTraceFor(events: readonly NonNullable<TableView['publicHistory']>[number][], accountId: string) {
  const turns: { drawSource: 'closed' | 'discard'; drawCard: string | null; discard: string | null }[] = [];
  let current: { drawSource: 'closed' | 'discard'; drawCard: string | null; discard: string | null } | null = null;
  for (const event of events) {
    if (!('player' in event) || event.player !== accountId) continue;
    if (event.type === 'drew') {
      current = { drawSource: event.source, drawCard: event.card, discard: null };
      turns.push(current);
    } else if (event.type === 'discarded' && current) {
      current.discard = event.card;
      current = null;
    }
  }
  return turns;
}

function displayCard(id: string | null): string {
  if (!id) return '—';
  const parsed = card(id);
  if (parsed.printedJoker) return '★';
  const suit = ({ S: '♠', H: '♥', D: '♦', C: '♣' } as const)[parsed.suit ?? 'S'];
  return `${parsed.rank}${suit}`;
}
